/**
 * TechStackPanel — lists tech stack items.
 * No background styling — used inline within project info column.
 *
 * TechStackSlot — smart animated wrapper driven by sectionScrollStore.
 * Crossfades content when the active section changes.
 */
import { useProjects } from "@/hooks/useProjects"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"
import { TECH_STACK_ICONS } from "@/lib/techStackIcons"
import gsap from "gsap"
import { useEffect, useRef, useState } from "react"

interface TechStackPanelProps {
  techStack: string[]
  contentRef?: React.Ref<HTMLDivElement>
}

export function TechStackPanel({ techStack, contentRef }: TechStackPanelProps) {
  if (techStack.length === 0) return null

  return (
    <div ref={contentRef} data-tech-stack className="flex flex-col items-end gap-2 pt-1">
      <div data-tech-stack-section className="flex flex-col items-end gap-2 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/30 [font-family:var(--font-sans)]">
          Tech Stack
        </h3>
        <div className="flex flex-wrap-reverse justify-end gap-1.5">
          {techStack.map((tech) => (
            <TechStackChip key={tech} tech={tech} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function TechStackChip({ tech }: { tech: string }) {
  return (
    <div
      data-tech-stack-chip
      className="flex h-12 items-center gap-2 border border-foreground/12 bg-background/72 px-3 text-[0.78rem] font-light uppercase tracking-[0.12em] text-foreground/78"
    >
      <div
        data-tech-stack-chip-icon
        className="flex size-7 items-center justify-center border border-foreground/10 bg-foreground/[0.03] p-1.5"
      >
        {TECH_STACK_ICONS[tech]
          ? <img src={`/logos/tech/${TECH_STACK_ICONS[tech]}.svg`} alt={tech} className="size-full object-contain" />
          : <div className="size-full bg-foreground/10" />
        }
      </div>
      <span>{tech}</span>
    </div>
  )
}

export function TechStackSlot() {
  const activeSectionIndex = useSectionScrollStore((s) => s.activeSectionIndex)
  const sections = useSectionScrollStore((s) => s.sections)
  const { data: projects } = useProjects()

  const [displayedIndex, setDisplayedIndex] = useState(0)
  const displayedIndexRef = useRef(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const displayedSection = sections[displayedIndex]
  const displayedProject =
    displayedSection?.type === "project" && projects
      ? (projects.find((p) => p.id === displayedSection.projectId) ?? null)
      : null

  useEffect(() => {
    if (activeSectionIndex === displayedIndexRef.current) return
    const el = contentRef.current
    if (!el) {
      displayedIndexRef.current = activeSectionIndex
      setDisplayedIndex(activeSectionIndex)
      return
    }
    gsap.killTweensOf(el)
    gsap.to(el, {
      opacity: 0,
      y: 8,
      duration: 0.14,
      ease: "power2.in",
      onComplete: () => {
        displayedIndexRef.current = activeSectionIndex
        setDisplayedIndex(activeSectionIndex)
        gsap.fromTo(el, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.22, ease: "power2.out" })
      },
    })
  }, [activeSectionIndex])

  return (
    displayedProject ? (
      <TechStackPanel
        techStack={displayedProject.techStack}
        contentRef={contentRef}
      />
    ) : null
  )
}
